import React from 'react';

// Brand colors
const CYAN_PRIMARY = 'var(--pm-cyan)';
const CYAN_DIM = 'var(--pm-cyan-glow)';

// Wave bar component for the waveform visualization
interface WaveformLineProps {
  large?: boolean;
  animationDelay?: number;
}

const WaveformLine: React.FC<WaveformLineProps> = ({ large = false, animationDelay = 0 }) => {
  const barHeights = large
    ? [0.4, 0.7, 0.5, 1, 0.6, 0.9, 0.5, 0.7, 0.4]
    : [0.5, 0.8, 0.6, 1, 0.7, 0.9, 0.5];

  const barWidth = large ? 4 : 3;
  const barGap = large ? 4 : 3;
  const maxHeight = large ? 32 : 20;

  return (
    <div
      className="flex items-center justify-center gap-[3px]"
      style={{ gap: `${barGap}px` }}
    >
      {barHeights.map((heightRatio, index) => (
        <div
          key={index}
          className="rounded-full"
          style={{
            width: `${barWidth}px`,
            height: `${maxHeight * heightRatio}px`,
            backgroundColor: large ? CYAN_PRIMARY : 'currentColor',
            animation: `waveOscillate 0.8s ease-in-out infinite`,
            animationDelay: `${animationDelay + index * 0.08}s`,
          }}
        />
      ))}
    </div>
  );
};

const MergingAnimation: React.FC = () => {
  return (
    <>
      {/* Keyframe animations */}
      <style>{`
        @keyframes waveOscillate {
          0%, 100% {
            transform: scaleY(1);
          }
          50% {
            transform: scaleY(0.6);
          }
        }

        @keyframes floatRight {
          0%, 100% {
            transform: translateX(0);
            opacity: 0.7;
          }
          50% {
            transform: translateX(10px);
            opacity: 1;
          }
        }

        @keyframes arrowPulse {
          0%, 100% {
            opacity: 0.5;
            transform: scaleX(1);
          }
          50% {
            opacity: 1;
            transform: scaleX(1.05);
          }
        }

        @keyframes glowPulse {
          0%, 100% {
            box-shadow: 0 0 20px ${CYAN_DIM}, 0 0 40px var(--pm-cyan-dim);
          }
          50% {
            box-shadow: 0 0 30px ${CYAN_DIM}, 0 0 60px var(--pm-cyan-accent);
          }
        }
      `}</style>

      <div className="flex items-center justify-center gap-6 py-4">
        {/* Input waves - 3 small waveforms stacked vertically */}
        <div className="flex flex-col gap-4">
          {[0, 0.3, 0.6].map((delay, index) => (
            <div
              key={index}
              className="text-muted-foreground/70"
              style={{
                animation: `floatRight 2s ease-in-out infinite`,
                animationDelay: `${delay}s`,
              }}
            >
              <WaveformLine animationDelay={delay} />
            </div>
          ))}
        </div>

        {/* Arrow pointing right */}
        <div
          className="flex items-center"
          style={{
            animation: 'arrowPulse 2s ease-in-out infinite',
          }}
        >
          <div
            className="h-[2px] w-12 rounded-full"
            style={{ backgroundColor: CYAN_DIM }}
          />
          <div
            className="w-0 h-0 -ml-1"
            style={{
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderLeft: `10px solid ${CYAN_DIM}`,
            }}
          />
        </div>

        {/* Output - single larger waveform with glow */}
        <div
          className="rounded-lg px-5 py-4"
          style={{
            border: `2px solid ${CYAN_PRIMARY}`,
            animation: 'glowPulse 2s ease-in-out infinite',
          }}
        >
          <WaveformLine large animationDelay={0.2} />
        </div>
      </div>
    </>
  );
};

export default MergingAnimation;
